import AsyncStorage from '@react-native-async-storage/async-storage';
import { AppState, Platform } from 'react-native';
import { deleteDevice } from '../data/settings';
import { supabase, queryClient, asyncStoragePersister } from '../data/client';
import { clearOrgIDCache } from '../data/organization';
import useKioskStore from '../data/store/useKioskStore';
import useGuideStore from '@/data/store/useGuideStore';
import * as Sentry from '@sentry/react-native';

const SENTRY_FLUSH_TIMEOUT_MS = 2000;
const PENDING_SIGN_OUT_CONTEXT_KEY = '@pendingSignOutContext';
let _signOutInProgress = false;

export const isSignOutInProgress = () => _signOutInProgress;

export const flushSentryWithTimeout = async () => {
    try {
        return await Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS);
    } catch {
        return false;
    }
};

export const reportPendingSignOutContext = async () => {
    try {
        const storedContext = await AsyncStorage.getItem(PENDING_SIGN_OUT_CONTEXT_KEY);
        if (!storedContext) return false;

        let parsedContext;
        try {
            parsedContext = JSON.parse(storedContext);
        } catch {
            return false;
        }

        const {reason, scope, platform, appState, timestamp} = parsedContext || {};
        if (![reason, scope, platform, appState, timestamp].every(value => typeof value === 'string')) return false;

        const signOutContext = {reason, scope, platform, appState, timestamp};
        Sentry.logger.warn('Recovered previous auth sign out context', signOutContext);

        const flushed = await flushSentryWithTimeout();
        if (!flushed) return false;

        try {
            const currentContext = await AsyncStorage.getItem(PENDING_SIGN_OUT_CONTEXT_KEY);
            if (currentContext === storedContext) {
                await AsyncStorage.removeItem(PENDING_SIGN_OUT_CONTEXT_KEY);
            }
        } catch {
            return true;
        }

        return true;
    } catch {
        return false;
    }
};

export const cleanupLocalAuthState = async () => {
    useGuideStore.getState().reset();
    try {
        clearOrgIDCache();
    } catch (error) {
        Sentry.logger.warn('Failed to clear organization cache during local auth cleanup', {error});
    }

    try {
        await AsyncStorage.removeItem("DEVICE_ID");
    } catch (error) {
        Sentry.logger.warn('Failed to clear device ID during local auth cleanup', {error});
    }

    try {
        useKioskStore.getState().clearKioskMode();
    } catch (error) {
        Sentry.logger.warn('Failed to clear kiosk mode during local auth cleanup', {error});
    }

    try {
        queryClient.clear();
        await asyncStoragePersister.removeClient();
    } catch (error) {
        Sentry.logger.warn('Failed to clear query cache during local auth cleanup', {error});
    }

    try {
        // Web-only private Notifications realtime channel: its broadcast
        // handlers close over the signed-out user, so it must be removed or
        // notifications keep firing after logout / into the next account.
        // Other channels (conversations, posts, check-in) own their lifecycle.
        const notificationChannels = supabase.getChannels()
            .filter(c => c?.subTopic?.startsWith('Notifications:'));
        await Promise.all(notificationChannels.map(c => supabase.removeChannel(c)));
    } catch (error) {
        Sentry.logger.warn('Failed to remove notifications realtime channel during local auth cleanup', {error});
    }

    Sentry.setUser(null);
};

export const signOut = async (scope = "local", reason = "unspecified") => {
    if (typeof window !== 'undefined' && window.__E2E_DISABLE_SESSION_REFRESH) {
        console.warn('[E2E signOut]', reason, scope, new Error().stack?.split('\n').slice(2, 6).join(' | '));
    }
    if (_signOutInProgress) return;
    _signOutInProgress = true;

    try {
        const signOutContext = {
            reason: typeof reason === 'string' ? reason : 'unspecified',
            scope: typeof scope === 'string' ? scope : 'local',
            platform: Platform.OS,
            appState: AppState.currentState || 'unknown',
            timestamp: new Date().toISOString(),
        };

        try {
            await AsyncStorage.setItem(PENDING_SIGN_OUT_CONTEXT_KEY, JSON.stringify(signOutContext));
        } catch (error) {
            console.warn('Failed to persist sign out context', error);
        }

        try {
            Sentry.logger.info("Explicit auth sign out requested", signOutContext);
            await flushSentryWithTimeout();
        } catch (error) {
            console.warn('Failed to report sign out context', error);
        }

        let deviceID;
        try {
            deviceID = await AsyncStorage.getItem("DEVICE_ID");
        } catch (error) {
            Sentry.logger.warn('Failed to read device ID during sign out', {error});
        }

        if(deviceID && deviceID !== "undefined" && deviceID !== "null"){
            try {
                await deleteDevice(deviceID);
            } catch (error) {
                // Device row may already be gone (e.g. removed from another device) —
                // don't let that block sign-out.
                Sentry.logger.warn('Failed to delete device row during sign out', {deviceID, error});
            }
        }

        await cleanupLocalAuthState();

        // E2E contexts share one saved session across parallel workers. Even
        // scope:'local' posts /logout and deletes the session server-side —
        // one spec's signOut would invalidate the file for every other worker.
        // Under the E2E flag just drop the persisted session locally; the
        // caller's own navigation sends the user back to /auth/login.
        if (typeof window !== 'undefined' && window.__E2E_DISABLE_SESSION_REFRESH) {
            const keys = await AsyncStorage.getAllKeys();
            await AsyncStorage.multiRemove(keys.filter(k => /^sb-.*-auth-token$/.test(k)));
        } else {
            await supabase.auth.signOut({scope});
        }
    } finally {
        _signOutInProgress = false;
    }
}